// Responsive utility functions and constants for consistent breakpoints
// Mobile: < 768px, Tablet: 768px - 1024px, Desktop: >= 1024px

export const BREAKPOINTS = {
  mobile: 768,
  tablet: 1024,
} as const;

export const RESPONSIVE_CLASSES = {
  // Layout containers
  container: 'p-3 sm:p-4 lg:p-6',
  cardContainer: 'p-3 sm:p-4 lg:p-6',
  modalContainer: 'p-4 sm:p-6',
  
  // Grid systems
  mainGrid: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6',
  cardGrid: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4',
  statsGrid: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4',
  formGrid: 'grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6',
  
  // Text sizing
  titleText: 'text-lg sm:text-xl',
  headerText: 'text-lg sm:text-xl',
  bodyText: 'text-sm sm:text-base',
  captionText: 'text-xs sm:text-sm',
  
  // Spacing
  marginBottom: 'mb-3 sm:mb-4',
  marginTop: 'mt-3 sm:mt-4',
  padding: 'p-3 sm:p-4',
  paddingY: 'py-3 sm:py-4',
  paddingX: 'px-3 sm:px-4',
  
  // Flex layouts
  flexContainer: 'flex flex-col sm:flex-row gap-3 sm:gap-4',
  flexItemsCenter: 'flex items-center gap-2 sm:gap-3',
  flexJustifyBetween: 'flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-4',
  
  // Button sizes
  buttonSize: 'text-sm sm:text-base px-3 sm:px-4 py-1.5 sm:py-2',
  iconSize: 'w-4 h-4 sm:w-5 sm:h-5',
  
  // Table responsiveness
  tableContainer: 'overflow-x-auto',
  mobileCardLayout: 'block lg:hidden',
  desktopTableLayout: 'hidden lg:block',
  
  // Hide/show at breakpoints
  hideOnMobile: 'hidden sm:block',
  hideOnTablet: 'hidden lg:block',
  hideOnDesktop: 'lg:hidden',
  showOnMobile: 'block sm:hidden',
  showOnTablet: 'hidden sm:block lg:hidden',
  showOnDesktop: 'hidden lg:block',
} as const;

export const getResponsiveModalWidth = () => ({
  xs: '95%',
  sm: '90%',
  md: '80%',
  lg: '70%',
  xl: '60%',
  xxl: '50%',
});

export const getResponsiveTableScroll = () => ({
  x: 'max-content',
  y: 400,
});

// Ant Design responsive table columns helper
export const getResponsiveColumnWidth = (baseWidth: number) => ({
  xs: Math.max(baseWidth * 0.8, 80),
  sm: Math.max(baseWidth * 0.9, 100),
  md: baseWidth,
  lg: baseWidth * 1.1,
  xl: baseWidth * 1.2,
});

// Hook for responsive behavior
export const useResponsive = () => {
  const getIsMobile = () => window.innerWidth < BREAKPOINTS.mobile;
  const getIsTablet = () => window.innerWidth >= BREAKPOINTS.mobile && window.innerWidth < BREAKPOINTS.tablet;
  const getIsDesktop = () => window.innerWidth >= BREAKPOINTS.tablet;
  
  return {
    isMobile: getIsMobile(),
    isTablet: getIsTablet(),
    isDesktop: getIsDesktop(),
  };
};

// Responsive pagination config
export const getResponsivePagination = () => ({
  pageSize: 10,
  showSizeChanger: false,
  showQuickJumper: false,
  size: 'small' as const,
  responsive: true,
  showTotal: (total: number, range: [number, number]) =>
    `${range[0]}-${range[1]} of ${total}`,
});
